import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import MascotCard from "../components/MascotCard";
import HomeChat from "../components/HomeChat";
import ReviewsSection from "../components/ReviewsSection";
import SocialProofBadge from "../components/SocialProofBadge";
import ContestBadge from "../components/ContestBadge";
import { MessageCircleHeart, Wind, Gamepad2, HeartPulse, BookHeart, Sparkles, Heart, Send, Check } from "lucide-react";
import { useLang } from "../lib/i18n";
import { track } from "../lib/ga";
import { amazonBookUrl } from "../lib/amazon";

function WaitlistForm() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sent
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    try {
      await api.post("/waitlist", { email });
      track.waitlistSignup(lang);
      setStatus("sent");
    } catch { /* ignore */ }
  };
  if (status === "sent") {
    return (
      <div className="text-sm text-[#73A38B] font-semibold inline-flex items-center gap-2 shrink-0" data-testid="waitlist-sent">
        <Check className="w-4 h-4" /> {t("home.coming.thanks")}
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex gap-2 shrink-0 w-full md:w-auto" data-testid="waitlist-form">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("home.coming.email.placeholder")}
        data-testid="waitlist-input"
        required
        className="min-w-0 flex-1 md:w-56 bg-white border border-[#E8E6E1] rounded-full px-4 py-2 text-sm text-[#3D405B] focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A]"
      />
      <button
        type="submit"
        data-testid="waitlist-submit"
        className="bg-[#3D405B] text-white rounded-full px-4 py-2 font-medium text-sm hover:bg-[#2f3147] transition-colors inline-flex items-center gap-1.5"
      >
        <Send className="w-3.5 h-3.5" /> {t("home.coming.notify")}
      </button>
    </form>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const [mascots, setMascots] = useState([]);
  const [mantra, setMantra] = useState("");

  useEffect(() => {
    api.get("/mascots").then((r) => setMascots(r.data)).catch(() => {});
    api.get("/mantras/random").then((r) => setMantra(r.data.mantra)).catch(() => {});
  }, [lang]);

  const quickActions = [
    { to: "/ask", label: t("home.action.ask"), desc: t("home.action.ask.desc"), Icon: MessageCircleHeart, bg: "bg-[#3D405B]", fg: "text-white", testId: "qa-chat" },
    { to: "/breathe", label: t("home.action.breathe"), desc: t("home.action.breathe.desc"), Icon: Wind, bg: "bg-[#81B29A]", fg: "text-white", testId: "qa-breathe" },
    { to: "/quiz", label: t("home.action.quiz"), desc: t("home.action.quiz.desc"), Icon: Gamepad2, bg: "bg-[#F2CC8F]", fg: "text-[#3D405B]", testId: "qa-quiz" },
    { to: "/mood", label: t("home.action.mood"), desc: t("home.action.mood.desc"), Icon: HeartPulse, bg: "bg-[#E07A5F]", fg: "text-white", testId: "qa-mood" },
  ];

  return (
    <div className="space-y-10" data-testid="home-page">
      <section className="relative overflow-hidden rounded-3xl bg-white border border-[#E8E6E1] p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F2CC8F]/30 blur-2xl" />
        <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-[#81B29A]/20 blur-2xl" />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4F1DE] text-[#3D405B] text-xs font-semibold uppercase tracking-wider" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" /> {t("home.hero.badge")}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#3D405B] mt-4 leading-[1.05]">
            {t("home.hero.title1")} <br />
            <span className="text-[#E07A5F]">{t("home.hero.title2")}</span>
          </h1>
          <p className="mt-5 text-lg text-[#3D405B]/80 max-w-2xl">
            {t("home.hero.subtitle")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/tips" data-testid="hero-cta-tips" className="bg-[#E07A5F] text-white rounded-full px-6 py-3 font-medium hover:bg-[#D56D52] transition-colors shadow-sm inline-flex items-center gap-2">
              <BookHeart className="w-4 h-4" /> {t("home.cta.explore")}
            </Link>
            <Link to="/breathe" data-testid="hero-cta-breathe" className="border-2 border-[#3D405B] text-[#3D405B] rounded-full px-6 py-3 font-medium hover:bg-[#3D405B] hover:text-white transition-colors inline-flex items-center gap-2">
              <Wind className="w-4 h-4" /> {t("home.cta.breathe")}
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <ContestBadge />
            <SocialProofBadge />
          </div>
        </div>
      </section>

      <HomeChat />

      {mantra && (
        <section className="rounded-3xl bg-[#3D405B] text-white p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.08)] relative overflow-hidden" data-testid="mantra-of-day">
          <Heart className="absolute -top-8 -right-8 w-40 h-40 text-white/5" strokeWidth={1} />
          <div className="text-xs uppercase tracking-widest text-[#F2CC8F] font-semibold">{t("home.mantra.label")}</div>
          <p className="font-heading text-2xl md:text-3xl mt-3 leading-snug relative">« {mantra} »</p>
        </section>
      )}

      <section data-testid="mascots-section">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#3D405B]">{t("home.mascots.title")}</h2>
            <p className="text-[#8A8D91] mt-1">{t("home.mascots.subtitle")}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6">
          {mascots.map((m) => <MascotCard key={m.id} mascot={m} />)}
        </div>

        {/* Sparky launch announcement (replaces old "Coming soon" waitlist) */}
        <div
          className="mt-6 relative overflow-hidden rounded-3xl border border-[#F4A261]/40 bg-gradient-to-r from-[#F2CC8F]/25 via-[#F4F1DE] to-[#F4A261]/15 p-6 md:p-7 flex flex-col md:flex-row items-center gap-5"
          data-testid="sparky-live-card"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#F4A261] grid place-items-center text-white shadow-lg text-3xl shrink-0">
            🦊
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4A261] text-white text-[10px] font-semibold uppercase tracking-widest">
              ✨ {t("home.sparky.badge")}
            </div>
            <div className="font-heading text-2xl text-[#3D405B] mt-2">{t("home.sparky.title")}</div>
            <div className="text-sm text-[#3D405B]/75 mt-1">{t("home.sparky.subtitle")}</div>
          </div>
          <Link
            to="/mascot/joy"
            data-testid="sparky-discover-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#2E3049] transition-colors shrink-0"
          >
            {t("home.sparky.cta")} <span>→</span>
          </Link>
        </div>
      </section>

      <section data-testid="quick-actions">
        <h2 className="font-heading text-3xl md:text-4xl text-[#3D405B] mb-6">{t("home.actions.title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {quickActions.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              data-testid={a.testId}
              className={`${a.bg} ${a.fg} rounded-3xl p-6 md:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 block`}
            >
              <a.Icon className="w-8 h-8" strokeWidth={2} />
              <div className="font-heading text-xl mt-4">{a.label}</div>
              <div className="text-sm opacity-80 mt-1">{a.desc}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="rounded-3xl bg-[#F4F1DE] p-8 md:p-10 border border-[#E8E6E1]" data-testid="safe-place-card">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-[#E07A5F] font-semibold">{t("home.safe.label")}</div>
          <h3 className="font-heading text-2xl md:text-3xl text-[#3D405B] mt-2">{t("home.safe.title")}</h3>
          <p className="text-[#3D405B]/80 mt-3">{t("home.safe.body")}</p>
        </div>
      </section>

      {/* Discreet KDP / book collection promo */}
      <section
        className="rounded-3xl bg-white border border-[#E8E6E1] p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        data-testid="books-promo-card"
      >
        <div className="flex -space-x-4 shrink-0" aria-hidden="true">
          {["anger", "anxiety", "confidence", "fear", "sadness", "joy"].map((e, i) => (
            <img
              key={e}
              src={`/covers/${e}.jpg`}
              alt=""
              className="w-14 h-14 md:w-16 md:h-16 rounded-2xl object-cover ring-4 ring-white shadow-md"
              style={{ transform: `rotate(${(i - 2.5) * 4}deg)` }}
            />
          ))}
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="text-xs uppercase tracking-widest text-[#81B29A] font-semibold">
            {t("home.books.badge")}
          </div>
          <h3 className="font-heading text-xl md:text-2xl text-[#3D405B] mt-1">{t("home.books.title")}</h3>
          <p className="text-[#3D405B]/75 text-sm mt-1">{t("home.books.body")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <a
            href={amazonBookUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="books-promo-link"
            className="bg-[#3D405B] text-white rounded-full px-5 py-3 font-medium hover:bg-[#2f3147] transition-colors text-sm whitespace-nowrap text-center"
          >
            {t("home.books.cta")}
          </a>
          <a
            href="https://pin.it/5a59BU1RV"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="books-pinterest-link"
            className="bg-white text-[#3D405B] border-2 border-[#3D405B] rounded-full px-5 py-3 font-medium hover:bg-[#E60023] hover:text-white hover:border-[#E60023] transition-colors text-sm whitespace-nowrap text-center inline-flex items-center gap-2 justify-center"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.42.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.03 0 1.52.77 1.52 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.18 1.79 2.18 2.14 0 3.79-2.26 3.79-5.53 0-2.89-2.08-4.9-5.04-4.9a5.22 5.22 0 0 0-5.44 5.24c0 1.04.4 2.15.9 2.75.1.12.11.22.08.34l-.34 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.44-2.89-2.44-4.65 0-3.78 2.75-7.26 7.92-7.26 4.16 0 7.4 2.96 7.4 6.93 0 4.13-2.61 7.46-6.23 7.46-1.22 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.14A12 12 0 1 0 12 0Z"/></svg>
            {t("home.books.pinterest")}
          </a>
        </div>
      </section>

      <ReviewsSection />
    </div>
  );
}
